// SynpRIS Design System - Soft Medical Theme
// Brandbook complet pentru UI consistent și prietenos

export const designSystem = {
  // =============================================================================
  // PALETA DE CULORI - Soft Medical
  // =============================================================================
  colors: {
    // Status Colors - Folosite pentru states și feedback
    status: {
      success: '#22c55e',    // Verde optimist (finalizat, success)
      error: '#ff6b6b',      // Roșu friendly (urgent, danger)  
      warning: '#fbbf24',    // Galben warm (pending, draft)
      info: '#4299e1',       // Albastru plăcut (info, links)
    },

    // Dark Theme
    dark: {
      background: {
        default: '#1a1d23',   // Mai warm decât negru pur
        paper: '#252930',     // Cards și formulare
        elevated: '#2d3340',  // Elevated surfaces
      },
      text: {
        primary: '#f8fafc',   // Titluri soft, mai puțin contrast
        secondary: '#a1a1aa', // Text secundar plăcut
        disabled: '#6b7280',  // Disabled elements
      },
      border: {
        default: '#374151',   // Border-uri normale
        light: '#4b5563',     // Border-uri mai vizibile
        focus: '#4299e1',     // Focus states
      },
      accent: {
        primary: '#60a5fa',   // Accent blue mai deschis
        hover: '#3b82f6',     // Hover states
      }
    },

    // Light Theme - Contrast îmbunătățit pentru vizibilitate  
    light: {
      background: {
        default: '#f8fafc',   // Puțin mai deschis pentru mai mult contrast
        paper: '#ffffff',     // Cards și formulare - alb pur
        elevated: '#f1f5f9',  // Elevated surfaces
      },
      text: {
        primary: '#1a202c',   // Mult mai întunecat pentru contrast
        secondary: '#4a5568', // Gri mai întunecat, dar still readable
        disabled: '#9ca3af',  // Puțin mai vizibil decât înainte
      },
      border: {
        default: '#e2e8f0',   // Border-uri discrete
        light: '#a0aec0',     // Border-uri mult mai vizibile  
        focus: '#3182ce',     // Focus states mai puternic
      },
      accent: {
        primary: '#3182ce',   // Accent blue mai întunecat
        hover: '#2c5aa0',     // Hover states mai întunecate
      }
    }
  },

  // =============================================================================
  // TYPOGRAPHY - Source Sans Pro, mai relaxat
  // =============================================================================
  typography: {
    fontFamily: '"Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    
    // Hierarchy clar și prietenos
    variants: {
      h1: {
        fontSize: '30px',
        fontWeight: 600,      // Semibold, nu Bold
        lineHeight: 1.3,
        letterSpacing: '-0.025em',
        marginBottom: '0px',  // Eliminăm margin pentru control manual
      },
      h2: {
        fontSize: '24px', 
        fontWeight: 500,      // Medium
        lineHeight: 1.35,
        letterSpacing: '-0.015em',
        marginBottom: '20px',
      },
      h3: {
        fontSize: '20px',
        fontWeight: 500,
        lineHeight: 1.4,
        marginBottom: '16px',
      },
      h4: {
        fontSize: '18px',
        fontWeight: 500,
        lineHeight: 1.4,
        marginBottom: '12px',
      },
      h5: {
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: 1.4,
        marginBottom: '8px',
      },
      h6: {
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1.4,
        marginBottom: '8px',
      },
      body1: {
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: 1.65,     // Mai generos pentru citibilitate
        letterSpacing: '0.01em',
      },
      body2: {
        fontSize: '14px', 
        fontWeight: 400,
        lineHeight: 1.6,
        letterSpacing: '0.01em',
      },
      caption: {
        fontSize: '12px',
        fontWeight: 500,      // Medium pentru labels
        lineHeight: 1.5,
        letterSpacing: '0.02em',
        textTransform: 'uppercase' as const,
      },
      button: {
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1,
        letterSpacing: '0.02em',
      }
    }
  },

  // =============================================================================
  // SPACING SYSTEM - Generos și consistent
  // =============================================================================
  spacing: {
    unit: 8,                // Base unit 8px
    micro: 4,              // 4px - foarte mici distanțe
    xs: 8,                 // 8px - small spacing
    sm: 12,                // 12px - medium-small
    md: 16,                // 16px - standard spacing
    lg: 24,                // 24px - large spacing  
    xl: 32,                // 32px - extra large
    xxl: 48,               // 48px - foarte large
    
    // Predefined patterns
    component: {
      padding: {
        small: 12,          // Buttons, inputs mici
        medium: 16,         // Cards, standard padding
        large: 24,          // Containers, sections
      },
      margin: {
        small: 8,
        medium: 16,
        large: 24,
        section: 32,        // Entre secțiuni majore
      }
    }
  },

  // =============================================================================
  // COMPONENT STANDARDS - Standardizări concrete
  // =============================================================================
  components: {
    // Buttons
    button: {
      borderRadius: 8,      // Consistent cu design-ul
      height: {
        small: 36,
        medium: 40,         // Standard
        large: 48,
      },
      padding: {
        horizontal: 24,
        vertical: 12,
      },
      textTransform: 'none' as const,
      fontWeight: 500,
    },

    // Input Fields
    input: {
      borderRadius: 8,
      height: 48,           // Standardizat
      padding: {
        horizontal: 16,
        vertical: 14,
      },
    },

    // Cards
    card: {
      borderRadius: 8,      // Standard, nu prea rotunjit
      padding: 16,          // Interior generos
      elevation: {
        default: 1,         // Subtle shadow
        hover: 2,           // Pe hover
        focus: 3,           // Pe focus
      }
    },

    // Layout
    layout: {
      maxWidth: 1200,       // Container max width
      sidebarWidth: 56,     // Collapsed sidebar
      navbarHeight: 64,     // Navbar height
      footerHeight: 40,     // Footer height
    }
  },

  // =============================================================================
  // ELEVATIONS & SHADOWS - Subtle și natural
  // =============================================================================
  shadows: {
    none: 'none',
    sm: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.1), 0 8px 10px rgba(0, 0, 0, 0.04)',
  },

  // =============================================================================
  // BREAKPOINTS - Responsive design
  // =============================================================================
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  }
} as const

// Export pentru usage ușor
export type DesignSystemType = typeof designSystem
export default designSystem
